import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TenantListItem } from '@/api/tenants/tenantsApi.types';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { PendingInvitationsList } from '@/components/admin/shared/PendingInvitationsList';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import { SCOPE_CODE } from '@/constants/scopes';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { usePendingInvitationsQuery } from '@/hooks/usePendingInvitationsQuery';
import { useTenantsQuery } from '@/hooks/useTenantsQuery';
import { CreateWorkspaceModal } from './CreateWorkspaceModal';

export function PractitionersPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(
    function resetPageOnSearchChange() {
      setPage(1);
    },
    [debouncedSearch]
  );

  const tenantsQuery = useTenantsQuery(page, debouncedSearch);
  const [invitationsPage, setInvitationsPage] = useState(1);
  const invitationsQuery = usePendingInvitationsQuery(SCOPE_CODE.tenant, invitationsPage);
  const canCreate = useHasPlatformPermission('tenant.create');
  const result = tenantsQuery.data;
  const pendingOwnerInvitations =
    invitationsQuery.data === undefined ? [] : invitationsQuery.data.items;
  const pendingOwnerInvitationsTotal =
    invitationsQuery.data === undefined ? 0 : invitationsQuery.data.totalItems;

  return (
    <div className="p-6 md:p-8">
      <AdminPageHeader
        title={t('admin:practitioners.title')}
        subtitle={t('admin:practitioners.subtitle')}
        action={
          canCreate === true ? (
            <Button
              onClick={function openModal() {
                setIsModalOpen(true);
              }}
            >
              <Icon name="Plus" />
              {t('admin:practitioners.newWorkspace')}
            </Button>
          ) : undefined
        }
      />

      <div className="mb-5">
        <ListSearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('admin:practitioners.searchPlaceholder')}
        />
      </div>

      {tenantsQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : tenantsQuery.isError === true || result === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            tenantsQuery.refetch();
          }}
        />
      ) : (
        <>
          <TenantsList tenants={result.items} hasSearch={debouncedSearch.trim().length > 0} />
          <ListPagination
            page={page}
            pageSize={ADMIN_LIST_PAGE_SIZE}
            totalItems={result.totalItems}
            onPageChange={setPage}
          />
        </>
      )}

      {pendingOwnerInvitationsTotal > 0 ? (
        <PendingInvitationsList
          invitations={pendingOwnerInvitations}
          title={t('admin:practitioners.pendingOwnerInvitations')}
          page={invitationsPage}
          totalItems={pendingOwnerInvitationsTotal}
          onPageChange={setInvitationsPage}
        />
      ) : null}

      {isModalOpen === true ? (
        <CreateWorkspaceModal
          onClose={function closeModal() {
            setIsModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface TenantsListProps {
  tenants: TenantListItem[];
  hasSearch: boolean;
}

function TenantsList({ tenants, hasSearch }: TenantsListProps) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();

  if (tenants.length === 0) {
    return (
      <Card padding="lg" shadow={false} className="text-center text-sm text-zoomuted">
        {hasSearch === true ? t('practitioners.noResults') : t('practitioners.emptyList')}
      </Card>
    );
  }

  return (
    <Card padding="none" className="divide-y divide-zooborder">
      {tenants.map(function renderTenant(tenant) {
        const isOrganization = tenant.type === 'organization';
        return (
          <button
            key={tenant.id}
            type="button"
            onClick={function openTenant() {
              navigate('/admin/practitioners/' + tenant.id);
            }}
            className="w-full text-left px-4 sm:px-5 py-4 flex items-center gap-3 hover:bg-zoovpalelavender hover:bg-opacity-30 transition-colors"
          >
            <div className="w-10 h-10 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
              <Icon name={isOrganization === true ? 'Building' : 'PawPrint'} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zooindigo truncate">{tenant.name}</div>
              {tenant.ownerName === null ? (
                <div className="text-[11px] text-zoomuted italic truncate">
                  {t('practitioners.noOwner')}
                </div>
              ) : (
                <div className="text-[11px] text-zoomuted truncate">
                  {tenant.ownerName}
                  {tenant.ownerEmail === null ? '' : ' · ' + tenant.ownerEmail}
                </div>
              )}
              <div className="text-[10px] text-zoomuted font-mono truncate">{tenant.code}</div>
            </div>
            <div className="hidden lg:flex flex-col items-end text-right shrink-0">
              <div className="text-[11px] font-semibold text-zooindigo">
                {t('practitioners.moduleCount', { count: tenant.activeModuleCount })}
              </div>
              <div className="text-[11px] text-zoomuted">
                {t('practitioners.joined', {
                  date: formatDateToDayMonthYear(new Date(tenant.createdAt)),
                })}
              </div>
            </div>
            <Badge tone="accent" size="sm" className="hidden sm:inline-flex shrink-0">
              {isOrganization === true
                ? t('practitioners.typeOrganization')
                : t('practitioners.typeSolo')}
            </Badge>
            <StatusBadge status={tenant.status} />
            <Icon name="ChevronRight" className="text-zoomuted shrink-0" />
          </button>
        );
      })}
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('admin');
  if (status === 'active') {
    return (
      <Badge tone="success" size="sm" className="shrink-0">
        {t('practitioners.statusActive')}
      </Badge>
    );
  }
  return (
    <Badge tone="muted" size="sm" className="shrink-0">
      {t('practitioners.statusSuspended')}
    </Badge>
  );
}
